/**
 * useModQueue — admin/founder mod queue access.
 * Reads from public.mod_queue (RLS gates to admin role) and resolves
 * items via the fn_resolve_mod_queue RPC.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ModQueueStatus = "open" | "dismissed" | "actioned";
export type ModQueuePriority = "low" | "normal" | "high" | "critical";
export type ModSurface = "dm" | "community" | "raela" | "profile";
export type ModSeverity = "info" | "warn" | "crisis" | "block";

export interface ModQueueItem {
  id: string;
  member_id: string;
  surface: ModSurface;
  message_id: string | null;
  severity: ModSeverity;
  action: string;
  rationale: string | null;
  excerpt: string | null;
  strike_count: number;
  priority: ModQueuePriority;
  status: ModQueueStatus;
  moderation_log_id: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
}

export const useModQueue = (status: ModQueueStatus = "open") => {
  const [items, setItems] = useState<ModQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("mod_queue")
      .select("*")
      .eq("status", status)
      .order("priority", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(200);
    if (err) setError(err.message);
    setItems((data as ModQueueItem[] | null) ?? []);
    setLoading(false);
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const resolve = useCallback(
    async (id: string, newStatus: "dismissed" | "actioned", note: string) => {
      const { error: err } = await supabase.rpc("fn_resolve_mod_queue", {
        _id: id,
        _status: newStatus,
        _note: note,
      });
      if (!err) setItems((prev) => prev.filter((i) => i.id !== id));
      return { error: err?.message ?? null };
    },
    [],
  );

  return { items, loading, error, reload: load, resolve };
};
